import { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import api from '../../api/client';
import type { User } from '../../types';

interface UserPickerProps {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  excludeIds?: number[];
}

export default function UserPicker({ selectedIds, onChange, excludeIds = [] }: UserPickerProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const res = await api.get('/api/v1/users/search?search=' + encodeURIComponent(query || '') + '&per_page=1000');
          setUsers(res.data?.data ?? []);
        } catch {
          // ignore
        } finally {
          setLoading(false);
        }
      };
      fetchUsers();
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => !excludeIds.includes(u.id));
  }, [users, excludeIds]);

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedIds.includes(u.id)),
    [users, selectedIds]
  );

  const toggleUser = (id: number) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  return (
    <div className="space-y-3">
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedUsers.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700"
            >
              {u.name} {u.surname}
              <button
                type="button"
                onClick={() => toggleUser(u.id)}
                className="rounded-full hover:bg-blue-100"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Kullanıcı ara..."
          className="w-full rounded-lg border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-sm outline-none focus:border-blue-500"
        />
      </div>

      <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <p className="px-3 py-2 text-sm text-slate-500">Yükleniyor...</p>
        ) : filteredUsers.length === 0 ? (
          <p className="px-3 py-2 text-sm text-slate-500">Kullanıcı bulunamadı.</p>
        ) : (
          filteredUsers.map((u) => {
            const selected = selectedIds.includes(u.id);
            return (
              <label
                key={u.id}
                data-testid={`user-picker-option-${u.id}`}
                className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm transition ${
                  selected ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleUser(u.id)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex flex-col">
                  <span className="font-medium">
                    {u.name} {u.surname}
                  </span>
                  <span className="text-xs text-slate-400">{u.email}</span>
                </div>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
